"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { technologyGroups } from "@/lib/data/site";
import { SectionHeader } from "@/components/ui/section-header";
import { cn } from "@/lib/utils";

const useCases: Record<string, string> = {
  OpenAI: "LLM applications, copilots, and multimodal interfaces.",
  Anthropic: "Reliable reasoning agents and enterprise assistants.",
  Gemini: "Multimodal analysis across documents and media.",
  Llama: "Private and fine-tuned open model deployments.",
  LangChain: "Composable LLM pipelines and retrieval flows.",
  LangGraph: "Stateful multi-agent orchestration graphs.",
  "Hugging Face": "Model hubs, fine-tuning, and evaluation tooling.",
  React: "High-performance product interfaces.",
  "Next.js": "Enterprise web platforms and content systems.",
  "Node.js": "API services and event-driven backends.",
  Python: "AI/ML services and data science workflows.",
  Java: "Mission-critical enterprise backends.",
  "Spring Boot": "Scalable microservice architectures.",
  FastAPI: "High-speed AI inference and data APIs.",
  PostgreSQL: "Transactional systems of record.",
  MongoDB: "Flexible document and product data stores.",
  Snowflake: "Cloud analytics and governed data sharing.",
  Databricks: "Lakehouse platforms for AI-ready data.",
  Kafka: "Real-time event streaming backbones.",
  Spark: "Large-scale processing and feature pipelines.",
  AWS: "Secure cloud foundations and managed AI services.",
  Azure: "Enterprise identity, data, and AI platforms.",
  "Google Cloud": "Data analytics and ML infrastructure.",
  Docker: "Portable application packaging.",
  Kubernetes: "Container orchestration at scale.",
  Terraform: "Infrastructure as code and drift control.",
  "GitHub Actions": "CI/CD automation and delivery pipelines.",
  ArgoCD: "GitOps continuous deployment.",
};

export function TechnologyCloud() {
  const all = useMemo(() => technologyGroups.flatMap((g) => g.items.map((item) => ({ item, category: g.category }))), []);
  const [selected, setSelected] = useState(all[0]);

  const related = useMemo(() => {
    const group = technologyGroups.find((g) => g.category === selected.category);
    return group?.items.filter((i) => i !== selected.item).slice(0, 4) ?? [];
  }, [selected]);

  return (
    <section className="relative py-16 sm:py-24 md:py-32">
      <div className="container-wide">
        <SectionHeader
          eyebrow="Technology Ecosystem"
          title={"AN INTERCONNECTED\nENGINEERING LANDSCAPE"}
          description="Select a technology to see its category, use cases, and relationships across the Maxridge stack."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="flex flex-wrap gap-2">
            {all.map(({ item, category }) => (
              <button
                key={item}
                type="button"
                onClick={() => setSelected({ item, category })}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm transition",
                  selected.item === item
                    ? "border-accent bg-accent/15 text-foreground"
                    : "border-border text-muted hover:border-border-strong hover:text-foreground",
                )}
              >
                {item}
              </button>
            ))}
          </div>

          <motion.div
            key={selected.item}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[2rem] border border-border bg-card p-7"
          >
            <p className="eyebrow mb-4">{selected.category}</p>
            <h3 className="font-display text-3xl tracking-tight">{selected.item}</h3>
            <p className="mt-4 text-sm text-muted leading-relaxed">
              {useCases[selected.item] ?? "Enterprise-ready capability within the Maxridge ecosystem."}
            </p>
            <div className="mt-8">
              <p className="mb-3 text-xs uppercase tracking-[0.16em] text-muted">Connected with</p>
              <div className="flex flex-wrap gap-2">
                {related.map((item) => (
                  <span key={item} className="rounded-full border border-border px-3 py-1 text-xs">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}